import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ImageManagerService } from "../../services/image-manager.service";

@Component({
  selector: "app-photo-dialog",
  templateUrl: "./photo-dialog.component.html",
  styleUrls: ["./photo-dialog.component.css"],
})
export class PhotoDialogComponent implements OnInit, OnDestroy {
  @Output() photoSaved = new EventEmitter<any>();

  photo: any;
  category: any;
  displayUrl: any;
  editable: boolean = false;
  currentRotation: number = 0;
  private blobUrl: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<PhotoDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { photo: any; category: any; editable?: boolean },
    private imageService: ImageManagerService
  ) {
    this.photo = data.photo;
    this.category = data.category;
    this.editable = data.editable || false;
  }

  ngOnInit() {
    this.prepareImageForDisplay();
  }

  ngOnDestroy() {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  }

  private prepareImageForDisplay() {
    // Se la foto ha già un displayUrl, usalo
    if (this.photo.displayUrl) {
      this.displayUrl = this.photo.displayUrl;
      return;
    }

    // Se la foto ha un file ArrayBuffer, convertilo in Blob e crea l'URL
    if (this.photo.file) {
      const blob = this.imageService.arrayBufferToBlob(
        this.photo.file,
        this.photo.type || "image/jpeg"
      );
      this.blobUrl = URL.createObjectURL(blob);
      this.displayUrl = this.imageService.generateSafeUrlFromBlob(blob);
      return;
    }

    // Se la foto è già un Blob, crea direttamente l'URL
    if (this.photo instanceof Blob) {
      this.blobUrl = URL.createObjectURL(this.photo);
      this.displayUrl = this.imageService.generateSafeUrlFromBlob(this.photo);
      return;
    }

    // Fallback: usa previewUrl se disponibile
    if (this.photo.previewUrl) {
      this.displayUrl = this.imageService.sanitizer.bypassSecurityTrustUrl(
        this.photo.previewUrl
      );
    }
  }

  downloadPhoto() {
    try {
      let blob: Blob;

      // Determina il blob da scaricare
      if (this.photo.file) {
        blob = this.imageService.arrayBufferToBlob(
          this.photo.file,
          this.photo.type || "image/jpeg"
        );
      } else if (this.photo instanceof Blob) {
        blob = this.photo;
      } else if (this.blobUrl) {
        // Crea un nuovo blob dall'URL esistente
        fetch(this.blobUrl)
          .then((response) => response.blob())
          .then((blobData) => {
            this.downloadBlob(blobData);
          })
          .catch((error) => {
            console.error("Errore nel download:", error);
          });
        return;
      } else {
        console.error("Impossibile scaricare la foto: formato non supportato");
        return;
      }

      this.downloadBlob(blob);
    } catch (error) {
      console.error("Errore nel download della foto:", error);
    }
  }

  private downloadBlob(blob: Blob) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Genera un nome file appropriato
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const categoryName =
      this.category.label.toLowerCase().replace(/\s+/g, "_") || "foto";
    link.download = `${categoryName}_${timestamp}.jpg`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Pulisci l'URL dopo il download
    URL.revokeObjectURL(url);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  onImageError(event: any) {
    console.error("Errore nel caricamento dell'immagine nel dialog");
    event.target.style.display = "none";
  }

  rotateLeft() {
    this.currentRotation = (this.currentRotation - 90) % 360;
    this.updateImageRotation();
  }

  rotateRight() {
    this.currentRotation = (this.currentRotation + 90) % 360;
    this.updateImageRotation();
  }

  private updateImageRotation() {
    const imgElement = document.querySelector(
      ".photo-image"
    ) as HTMLImageElement;
    if (imgElement) {
      imgElement.classList.add("rotating");
      imgElement.style.transform = `rotate(${this.currentRotation}deg)`;

      // Rimuovi la classe dopo l'animazione
      setTimeout(() => {
        imgElement.classList.remove("rotating");
      }, 500);
    }
  }

  saveRotatedImage() {
    console.log("saveRotatedImage chiamato, editable:", this.editable);
    if (!this.editable) {
      console.log("Editable è false, esco dal metodo");
      return;
    }
    console.log("Procedo con il salvataggio...");
    try {
      // Crea un canvas per applicare la rotazione
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onerror = (error) => {
        console.error("Errore nel caricamento dell'immagine:", error);
        console.error("URL dell'immagine:", this.displayUrl);
      };

      // Salva il valore di rotazione in una variabile locale
      const rotationAngle = this.currentRotation;
      console.log("Angolo di rotazione salvato:", rotationAngle);

      img.onload = () => {
        console.log(
          "Immagine caricata con successo, dimensioni:",
          img.width,
          "x",
          img.height
        );
        console.log("Angolo di rotazione da applicare:", rotationAngle);
        // Calcola le dimensioni del canvas in base alla rotazione
        const isRotated90or270 = rotationAngle === 90 || rotationAngle === 270;
        canvas.width = isRotated90or270 ? img.height : img.width;
        canvas.height = isRotated90or270 ? img.width : img.height;

        if (ctx) {
          // Sposta il punto di rotazione al centro
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((rotationAngle * Math.PI) / 180);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);

          // Converti il canvas in blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Converti il blob in ArrayBuffer usando FileReader
                const reader = new FileReader();
                reader.onload = () => {
                  const arrayBuffer = reader.result as ArrayBuffer;

                  // Aggiorna l'URL di visualizzazione
                  this.blobUrl = URL.createObjectURL(blob);
                  this.displayUrl =
                    this.imageService.generateSafeUrlFromBlob(blob);

                  // Crea un oggetto con i dati aggiornati
                  const updatedPhoto = {
                    ...this.photo,
                    file: arrayBuffer,
                    type: blob.type,
                    displayUrl: this.displayUrl,
                  };

                  // Emetti l'evento con la foto aggiornata
                  this.photoSaved.emit(updatedPhoto);

                  // // Reset della rotazione
                  // this.currentRotation = 0;
                  // this.updateImageRotation();
                  setTimeout(() => {
                    this.dialogRef.close();
                  }, 500);
                  console.log("Immagine salvata con rotazione applicata");
                };
                reader.readAsArrayBuffer(blob);
              }
            },
            "image/jpeg",
            0.9
          );
        }
      };

      // Estrai l'URL effettivo dal SafeUrl
      const actualUrl = this.displayUrl.changingThisBreaksApplicationSecurity;
      console.log("Tentativo di caricamento immagine con URL:", actualUrl);
      img.src = actualUrl;
    } catch (error) {
      console.error("Errore nel salvataggio dell'immagine ruotata:", error);
    }
  }
}
